import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { KYMW1200Model } from '../kymw1200.model';
import { Injectable } from '@angular/core';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';

// レスポンス構造
export type GetBuyerSelectItemsResponse = {
  Code: string;
  Name: string;
  ClassCodeList?: string[];
}[];

/**
 * バイヤー選択項目取得サービス
 * @export GetBuyerSelectItemsService
 * @class GetBuyerSelectItemsService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class GetBuyerSelectItemsService extends PlessJsonResponderSub<GetBuyerSelectItemsResponse> {
  model: KYMW1200Model;
  private succeed: () => void;
  private fail: () => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof GetBuyerSelectItemsService
   */
  private getBody(): PlessRequest {
    // getStaffBuyerSelectItemsで取得したバイヤーコードを、重複を排除したカンマ区切りのデータに整形し設定
    const uniqueBuyerCodesList = Array.from(new Set(this.model.staffBuyerList.map(sb => sb.BuyerCode)));
    // 重複を排除したリストからカンマ区切りの文字列に変換
    const buyerCodes = uniqueBuyerCodesList.join(',');

    const param = {
      SupplierCode: this.model.isSupplier ? this.model.userSupplierCode : null,
      BuyerCodes: buyerCodes,
    };
    if (!param.SupplierCode) delete param.SupplierCode;
    if (!param.BuyerCodes) delete param.BuyerCodes;

    if (!param.SupplierCode && !param.BuyerCodes) {
      return new PlessRequest();
    } else {
      return new PlessRequest({ parameter: param });
    }
  }

  /**
   * API呼び出し
   *
   * @param model
   * @param f
   * @memberof GetBuyerSelectItemsService
   */
  execute(
    model: KYMW1200Model,
    s: () => void = null,
    f: () => void = null
  ): void {
    this.model = model;
    this.succeed = s;
    this.fail = f;
    this.send('getBuyerSelectItems', this.getBody());
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof GetBuyerSelectItemsService
   */
  success(data: GetBuyerSelectItemsResponse) {
    // 部門長かつAPIレスポンスstatusが"ok"の場合、APIレスポンスをバイヤーセレクトリストに設定する。
    if (this.model.isLeader) {
      // バイヤーセレクトリストのバイヤーコードとgetStaffBuyerSelectItemsで保持したバイヤーコードで合致した部門コードを部門リストに追加する。
      // 部門長はレスポンスパラメータ＋部門リスト（配列）の形式となる
      let tmpBuyerList = [];
      for (const _ of data) {
        // 対象のバイヤーコードの持つ部門コードをリストにする
        let buyerRelatedClassCodeList: any[] = [];
        this.model.staffBuyerList.map(sb => {
          if (sb.BuyerCode === _.Code && !buyerRelatedClassCodeList.includes(sb.ClassCode)) {
            buyerRelatedClassCodeList.push(sb.ClassCode);
          }
        });
        tmpBuyerList.push({
          Code: _.Code,
          Name: _.Code + ':' + _.Name,
          ClassCodeList: buyerRelatedClassCodeList
        });
      }
      this.model.buyerList = tmpBuyerList;

      // 取得できた部門コード数分（重複は排除）、項目に"部門:部門コードの配下全バイヤー"を追加する。
      // ※部門コードが複数存在しない場合、"部門：部門コード配下全バイヤー"の選択肢は追加しない。
      // 部門コードリスト
      const classCodeList = Array.from(new Set(this.model.staffBuyerList.map(sb => sb.ClassCode)));
      // 部門コードが複数存在する場合、項目に"部門:部門コードの配下全バイヤー"を追加
      if (classCodeList.length > 1) {
        classCodeList.sort();
        classCodeList.map(classCode => {
          this.model.buyerList.push({
            Code: KYMW1200Model.CLASS_ALL_BUYER_CODE + classCode,
            Name: KYMW1200Model.CLASS_ALL_BUYER_NAME.replace('{0}', classCode),
          })
        })
      }
      // 取得できたバイヤーコードが1件以上の場合、項目に"配下全バイヤー"を追加する。
      if (this.model.buyerList.length > 0) {
        this.model.buyerList.push({
          Code: KYMW1200Model.ALL_BUYER_CODE,
          Name: KYMW1200Model.ALL_BUYER_NAME,
        })
      }
    } else {
      // 部門長以外でAPIレスポンスstatusが"ok"の場合、APIレスポンスをバイヤーセレクトリストに設定/表示する。
      this.model.buyerList = data.map((_) => {
        return {
          Code: _.Code,
          Name: _.Code + ':' + _.Name
        }
      });
      // 取得できたバイヤーコードが1件以上の場合、項目に"配下全バイヤー"を追加する。
      if (this.model.buyerList.length > 0) {
        this.model.buyerList.push({
          Code: KYMW1200Model.ALL_BUYER_CODE,
          Name: KYMW1200Model.ALL_BUYER_NAME,
        })
      }
    }
    if (this.succeed) this.succeed();
  }

  /**
   * async/await 記述用
   *
   * @param model
   * @returns Promise<void>
   */
  public executeAsync(model: KYMW1200Model): Promise<void> {
    return new Promise((resolve, reject) => {
      this.execute(
        model,
        () => resolve(),
        () => reject()
      );
    });
  }

  protected statusError(status: string, result: any) {
    super.statusError(status, result);
    if (this.fail) this.fail();
  }
}
